/**
 * Spurwechsel PRO — Logo System
 * 
 * Offizielles Logo: Stilisiertes X-Icon + "Spurwechsel" Serif + "PRO" Badge
 * Primärfarbe: Deep Forest Green #1A582E
 * Text: Dark Gray #2A2A2A
 * 
 * Icon: Transparentes PNG des offiziellen X-Symbols (gefüllte Blattform)
 * Fallback: SVG mit gefüllten Pfaden
 * 
 * Varianten:
 * - SpurwechselIcon: X-Symbol allein (Favicon, Chat-Header, Mobile, kleine Kontexte)
 * - SpurwechselLogo: Vollständiges Logo (Icon + Schrift + PRO Badge)
 * 
 * Größen-System:
 * - xs:      Icon 20px — Inline/Chat
 * - small:   Icon 24px, Text 15px — Mobile Nav, Footer
 * - default: Icon 28px, Text 18px — Desktop Nav, Standard
 * - large:   Icon 36px, Text 24px — Landing Hero, Login
 * - xl:      Icon 48px, Text 32px — Onboarding, leere Zustände
 * 
 * Varianten:
 * - dark: Grünes Icon, dunkler Text (auf hellem Hintergrund)
 * - light: Weißes Icon, weißer Text (auf dunklem/grünem Hintergrund)
 */

const BRAND = {
  green: "#1A582E",
  text: "#2A2A2A",
  white: "#FFFFFF",
  badgeBg: "#1A582E",
  badgeText: "#FFFFFF",
};

/**
 * SpurwechselIcon — Das offizielle X-Symbol
 * Verwendet das transparente PNG für pixel-perfekte Darstellung.
 * SVG-Fallback für Kontexte ohne Bildzugang (z.B. E-Mails).
 */
export function SpurwechselIcon({ className = "w-7 h-7", variant = "dark" }) {
  // Use transparent PNG for the icon — pixel-perfect rendering
  const filterStyle = variant === "light"
    ? { filter: "brightness(0) invert(1)" }
    : {};

  return (
    <img
      src="/icon-transparent.png"
      alt="Spurwechsel"
      className={`${className} object-contain`}
      style={filterStyle}
      data-testid="spurwechsel-icon"
      draggable="false"
    />
  );
}

/**
 * SpurwechselIconSvg — SVG-Variante für Inline/E-Mail Kontexte
 * Gefüllte Blattform-Pfade, die das offizielle Icon nachbilden.
 */
export function SpurwechselIconSvg({ className = "w-7 h-7", color }) {
  const fill = color || BRAND.green;
  return (
    <svg
      viewBox="0 0 48 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Spurwechsel"
    >
      {/* Blade 1: bottom-left → top-right (filled crescent) */}
      <path
        d="M2 36 Q14 22 24 20 Q34 18 46 4 Q34 14 24 20 Q14 26 2 36Z"
        fill={fill}
      />
      {/* Blade 2: top-left → bottom-right (filled crescent) */}
      <path
        d="M2 4 Q14 18 24 20 Q34 22 46 36 Q34 26 24 20 Q14 14 2 4Z"
        fill={fill}
      />
    </svg>
  );
}

const SIZE_MAP = {
  xs: { icon: "w-5 h-5", text: "text-[13px]", gap: "gap-1", badge: "text-[7px] px-1 py-[1px]", badgeGap: "ml-1" },
  small: { icon: "w-6 h-6", text: "text-[15px]", gap: "gap-1.5", badge: "text-[8px] px-1.5 py-[1px]", badgeGap: "ml-1" },
  default: { icon: "w-7 h-7", text: "text-lg", gap: "gap-2", badge: "text-[9px] px-1.5 py-[2px]", badgeGap: "ml-1.5" },
  large: { icon: "w-9 h-9", text: "text-2xl", gap: "gap-2.5", badge: "text-[10px] px-2 py-[2px]", badgeGap: "ml-2" },
  xl: { icon: "w-12 h-12", text: "text-3xl", gap: "gap-3", badge: "text-[11px] px-2 py-[3px]", badgeGap: "ml-2" },
};

export function SpurwechselLogo({ className = "", size = "default", variant = "dark", showBadge = true }) {
  const s = SIZE_MAP[size] || SIZE_MAP.default;
  const textColor = variant === "light" ? BRAND.white : BRAND.text;
  const badgeBg = variant === "light" ? "rgba(255,255,255,0.2)" : BRAND.badgeBg;
  const badgeColor = BRAND.white;

  return (
    <div className={`flex items-center ${s.gap} ${className}`} data-testid="spurwechsel-logo">
      <SpurwechselIcon className={s.icon} variant={variant} />
      <div className="flex items-center">
        <span
          className={`font-heading ${s.text} font-semibold tracking-[0.01em] leading-none`}
          style={{ color: textColor, fontWeight: 600 }}
        >
          Spurwechsel
        </span>
        {showBadge && (
          <span
            className={`${s.badgeGap} ${s.badge} font-body font-bold tracking-wider rounded uppercase leading-none inline-flex items-center`}
            style={{
              backgroundColor: badgeBg,
              color: badgeColor,
              borderRadius: "4px",
              letterSpacing: "0.08em",
            }}
          >
            PRO
          </span>
        )}
      </div>
    </div>
  );
}

/** Nur der Text "Spurwechsel PRO" ohne Icon — für Textkontext */
export function SpurwechselWordmark({ className = "", size = "default" }) {
  const textSize = size === "large" ? "text-2xl" : size === "small" ? "text-sm" : "text-lg";
  return (
    <span className={`font-heading ${textSize} font-semibold tracking-[0.01em] ${className}`} style={{ color: BRAND.text, fontWeight: 600 }}>
      Spurwechsel<span className="font-body text-[0.55em] font-bold tracking-wider ml-1.5 px-1.5 py-0.5 rounded text-white inline-block align-middle" style={{ backgroundColor: BRAND.green, fontSize: "0.5em", borderRadius: "4px" }}>PRO</span>
    </span>
  );
}
